import React from 'react';
import {
  GitCommit,
  Bitcoin,
  Scale,
  ArrowRight,
  ShieldCheck,
  FileCode2,
  Lock,
  CheckCircle2,
  FileBadge,
} from 'lucide-react';

import {
  Badge,
  Box,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

function ConceptStep({
  step,
  icon,
  iconColor,
  title,
  description,
  micro,
}) {
  return (
    <Paper withBorder radius="lg" p="lg" h="100%">
      <Stack gap="md" h="100%">
        <Group justify="space-between" align="center">
          <Badge variant="light" color="gray" radius="xl">
            {step}
          </Badge>
          <ThemeIcon variant="light" color={iconColor} size={56} radius="md">
            {icon}
          </ThemeIcon>
        </Group>

        <Title order={3}>{title}</Title>

        <Text c="dimmed" fz="sm">
          {description}
        </Text>

        <Paper withBorder p="sm" radius="md" mt="auto">
          {micro}
        </Paper>
      </Stack>
    </Paper>
  );
}

function ValueProp({ icon, title, description, color = 'teal' }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Group align="flex-start" wrap="nowrap" gap="sm">
        <ThemeIcon variant="light" color={color} radius="xl">
          {icon}
        </ThemeIcon>
        <Stack gap={4}>
          <Text fw={700}>{title}</Text>
          <Text c="dimmed" fz="sm">
            {description}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}

export default function ConceptInfographic() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');

  return (
    <Box py={40}>
      <Container size="lg">
        <Stack gap="xl">
          <Stack align="center" justify="center" gap="sm">
            <Badge
              variant="light"
              color="teal"
              size="lg"
              radius="xl"
              leftSection={<ShieldCheck size={16} />}
            >
              {t('Mathematically Guaranteed')}
            </Badge>

            <Title order={1} fw={800} ta="center" maw={900}>
              {t('Secure Your Prior Art')}
              <br />
              {t('Without Exposing Code')}
            </Title>

            <Text c="dimmed" size="lg" maw={820} ta="center">
              {t('We turn your everyday Git commits into irrefutable legal evidence. Prove exactly ')}
              <Text component="span" c="teal" fw={700}>
                {t('when')}
              </Text>
              {t(' you had an idea, without ever showing us the idea itself.')}
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
            <ConceptStep
              step="1"
              icon={<GitCommit size={30} />}
              iconColor="blue"
              title={t('The Fingerprint')}
              description={t('When you commit code, Git generates a unique, one-way hash representing the exact state of your entire repository.')}
              micro={
                <Group gap="xs" wrap="nowrap">
                  <FileCode2 size={14} />
                  <Text ff="monospace" fz="sm">
                    commit
                  </Text>
                  <Badge variant="light" color="blue">
                    a1b2c3d
                  </Badge>
                </Group>
              }
            />

            <ConceptStep
              step="2"
              icon={<Bitcoin size={30} />}
              iconColor="orange"
              title={t('The Anchor')}
              description={t('We extract only the hash and cryptographically embed it into a Bitcoin transaction, freezing its existence in time.')}
              micro={
                <Group gap="xs" wrap="nowrap">
                  <ArrowRight size={14} />
                  <Text fw={700} fz="sm" c="orange">
                    {t('Anchored in Block #834102')}
                  </Text>
                </Group>
              }
            />

            <ConceptStep
              step="3"
              icon={<Scale size={30} />}
              iconColor="teal"
              title={t('The Proof')}
              description={t('You receive an immutable `.ots` receipt. You can now mathematically prove to any lawyer, auditor, or court that your idea existed on this date.')}
              micro={
                <Group gap="xs" wrap="nowrap">
                  <FileBadge size={14} />
                  <Text fw={700} fz="sm" c="teal">
                    {t('Legal Prior Art Verified')}
                  </Text>
                </Group>
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <ValueProp
              icon={<Lock size={16} />}
              color="teal"
              title={t('Zero-Knowledge Security')}
              description={t('We never see, copy, or store your actual source code. We only mathematically stamp the public Git hash.')}
            />

            <ValueProp
              icon={<CheckCircle2 size={16} />}
              color={colorScheme === 'dark' ? 'lime' : 'green'}
              title={t('Vendor Independent')}
              description={t('The cryptographic proof relies entirely on Bitcoin math. You can verify it locally even if our company disappears.')}
            />

            <ValueProp
              icon={<Scale size={16} />}
              color="grape"
              title={t('Court-Ready Evidence')}
              description={t('Protect your intellectual property from patent trolls and IP theft with globally recognized cryptographic standards.')}
            />
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}
